import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { testimonials } from '@/mocks/home';

export default function AboutPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    website_alt: '',
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.website_alt.trim() !== '') {
      setFormStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '', website_alt: '' });
      return;
    }

    if (formData.message.length > 500) {
      setFormError('留言内容不能超过500个字符');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formEl = e.currentTarget as HTMLFormElement;
      const fd = new FormData(formEl);
      fd.delete('website_alt');

      const response = await fetch('https://readdy.ai/api/form/d9u476t3pcjqs2fcslog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '', website_alt: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Page Header */}
          <div className="py-8 md:py-12">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              关于蜜桃在线
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl leading-relaxed">
              蜜桃在线致力于打造国内领先的高清视频内容平台，以海量正版资源、流畅播放体验和优质服务，为数千万用户带来极致的在线娱乐享受。
            </p>
          </div>

          {/* Brand Introduction */}
          <section className="pb-12 md:pb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
              品牌介绍
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-4">
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  蜜桃在线成立于2023年，总部位于北京，是一家专注于高清视频内容聚合与分发的互联网科技公司。平台汇聚了电影、电视剧、综艺、动漫、纪录片、体育、音乐、游戏等八大类别的海量视频资源，日均更新内容超过1000部。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  我们拥有一支由资深视频行业专家和顶尖技术人才组成的团队，通过自主研发的智能推荐算法和全球CDN加速网络，为用户提供个性化、高品质、流畅稳定的视频观看体验。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  截至2026年，蜜桃在线已累计服务超过2000万注册用户，月活跃用户突破800万，平台日均播放量超过5000万次，稳居国内视频平台第一梯队。
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-6 rounded-xl bg-primary-50 border border-primary-100">
                  <div className="text-3xl font-bold font-heading text-primary-600 mb-1">2000万+</div>
                  <div className="text-sm text-foreground-500">注册用户</div>
                </div>
                <div className="p-6 rounded-xl bg-accent-50 border border-accent-100">
                  <div className="text-3xl font-bold font-heading text-accent-600 mb-1">50万+</div>
                  <div className="text-sm text-foreground-500">视频资源</div>
                </div>
                <div className="p-6 rounded-xl bg-secondary-50 border border-secondary-100">
                  <div className="text-3xl font-bold font-heading text-secondary-600 mb-1">99.9%</div>
                  <div className="text-sm text-foreground-500">服务可用率</div>
                </div>
                <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="text-3xl font-bold font-heading text-foreground-800 mb-1">7x24</div>
                  <div className="text-sm text-foreground-500">客服支持</div>
                </div>
              </div>
            </div>
          </section>

          {/* Mission & Vision */}
          <section className="pb-12 md:pb-16">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              <div className="p-6 md:p-8 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-4">
                  <i className="ri-focus-2-line text-xl"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">我们的使命</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  让每个人都能轻松发现和畅享优质视频内容，用科技连接内容与用户，打造最懂你的娱乐平台。
                </p>
              </div>
              <div className="p-6 md:p-8 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4">
                  <i className="ri-eye-2-line text-xl"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">我们的愿景</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  成为全球华语用户首选的视频内容平台，用创新技术和优质内容持续引领行业变革。
                </p>
              </div>
              <div className="p-6 md:p-8 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 mb-4">
                  <i className="ri-heart-2-line text-xl"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">核心价值观</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  用户至上、内容为王、创新驱动、合作共赢——这是我们始终坚持的四大核心价值观。
                </p>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="pb-12 md:pb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((t, i) => (
                <div
                  key={i}
                  className="p-6 rounded-xl bg-background-100 border border-background-200/70"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-12 h-12 rounded-full object-cover"
                      title={`${t.name} - 蜜桃在线用户`}
                    />
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                      <div className="flex gap-0.5 mt-0.5">
                        {Array.from({ length: t.rating }).map((_, j) => (
                          <div key={j} className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-star-fill text-accent-500 text-xs"></i>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">&ldquo;{t.comment}&rdquo;</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Form */}
          <section className="pb-14 md:pb-20">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Contact Info */}
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0 mt-0.5">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900 mb-1">公司地址</div>
                    <div className="text-sm text-foreground-500">北京市朝阳区望京科技园A座18层</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0 mt-0.5">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900 mb-1">电子邮箱</div>
                    <div className="text-sm text-foreground-500">contact@mitaozaixian.com</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0 mt-0.5">
                    <i className="ri-customer-service-2-line"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900 mb-1">客服热线</div>
                    <div className="text-sm text-foreground-500">400-888-6666（7x24小时）</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-200 text-foreground-600 flex-shrink-0 mt-0.5">
                    <i className="ri-time-line"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900 mb-1">工作时间</div>
                    <div className="text-sm text-foreground-500">周一至周日 9:00 - 21:00</div>
                  </div>
                </div>
              </div>

              {/* Form */}
              <div className="p-6 md:p-8 rounded-xl bg-background-100 border border-background-200/70">
                <h3 className="text-base font-semibold text-foreground-900 mb-5">发送留言</h3>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="flex flex-col gap-4"
                >
                  <div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="您的姓名 *"
                      required
                      className="w-full px-4 py-3 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="您的邮箱 *"
                      required
                      className="w-full px-4 py-3 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="留言主题 *"
                      required
                      className="w-full px-4 py-3 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="请输入您的留言内容（不超过500字）*"
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    ></textarea>
                    <div className="text-xs text-foreground-400 mt-1 text-right">
                      {formData.message.length}/500
                    </div>
                  </div>
                  <input
                    type="text"
                    name="website_alt"
                    value={formData.website_alt}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="hp-field"
                  />
                  {formError && (
                    <div className="text-sm text-secondary-600 bg-secondary-50 px-4 py-2.5 rounded-lg">
                      {formError}
                    </div>
                  )}
                  {formStatus === 'success' && (
                    <div className="text-sm text-accent-700 bg-accent-50 px-4 py-2.5 rounded-lg">
                      留言已发送成功！我们会尽快回复您。
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="px-6 py-3 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 disabled:opacity-60 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'loading' ? '发送中...' : '提交留言'}
                  </button>
                </form>
              </div>
            </div>
          </section>
        </div>

        {/* JSON-LD Structured Data for Organization */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Organization',
              name: '蜜桃在线',
              url: 'https://peachshow.github.io',
              description: '蜜桃在线是国内领先的高清视频内容平台，提供电影、电视剧、综艺、动漫等海量视频资源。',
              email: 'contact@mitaozaixian.com',
              telephone: '+86-400-888-6666',
              address: {
                '@type': 'PostalAddress',
                addressLocality: '北京',
                addressRegion: '朝阳区',
                streetAddress: '望京科技园A座18层',
                addressCountry: 'CN',
              },
              sameAs: [
                'https://weibo.com/mitaozaixian',
                'https://space.bilibili.com/mitaozaixian',
              ],
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}